'use client';

import Breadcrumbs from 'components/breadcrumbs';
import MinimalHeader from 'components/headers/minimal-header';
import InlineAnnouncement from 'components/inline-announcements';
import TabDrawers from 'components/tab-drawer';
import PageSection from 'components/containers/page-section';
import { getUrlString } from 'helpers/helpers';
import ArtworkTabPanel from './tabs/artwork';
import AboutTabPanel from './tabs/about';
import ResearchLinksTabPanel from './tabs/research-links';
import RelatedTabPanel, { type RelatedContent } from './tabs/related-content';
import styles from './collection-object-package-page.module.scss';
import type { PortableTextBlock } from '@portabletext/types';
import type {
	BreadcrumbSegment,
	IJSONFriendlyObjectPackage,
	SanityAnnouncement,
	SanityMetadataFields,
	SanityPromo,
} from '@metmuseum/types';

const CollectionObjectPackagePage = ({
	filter,
	objectPackage,
	title,
	subtitle,
	about,
	announcements,
	suggestedLinks,
	segments,
	perLoad,
	researchLinks = [],
	relatedContent = {},
}: ICollectionObjectPackagePageProps) => {
	const links = suggestedLinks?.map((link) => ({
		href: getUrlString(link),
		text: link.cta,
	}));
	const tabs = [
		{
			name: 'Artwork',
			body: (
				<ArtworkTabPanel
					filter={filter}
					objectPackage={objectPackage}
					perLoad={perLoad}
				/>
			),
		},
	];

	if (about?.length) {
		tabs.push({
			name: 'About',
			body: <AboutTabPanel about={about} />,
		});
	}

	if (researchLinks?.length) {
		tabs.push({
			name: 'Researching the Collection',
			body: <ResearchLinksTabPanel promos={researchLinks} />,
		});
	}

	if (
		Object.keys(relatedContent)?.some((key) => relatedContent[key]?.length > 0)
	) {
		tabs.push({
			name: 'Related Content',
			body: <RelatedTabPanel relatedContent={relatedContent} />,
		});
	}

	return (
		<main>
			<Breadcrumbs segments={segments} />
			<MinimalHeader links={links} heading={title} description={subtitle} />
			{announcements?.length > 0 && (
				<PageSection>
					<InlineAnnouncement announcements={announcements} />
				</PageSection>
			)}
			<PageSection hasClampedWidth={false}>
				{tabs.length > 1 ? (
					<TabDrawers
						hasDivider={false}
						tabs={tabs}
						inputGroupName={'collection-tabs'}
						screenreaderLegendText="View More Collection Info"
						tabBodyWrapperClass={styles.tabBodyWrapper}
					/>
				) : (
					tabs[0].body
				)}
			</PageSection>
		</main>
	);
};

export default CollectionObjectPackagePage;

export interface ICollectionObjectPackagePageProps {
	filter?: boolean;
	objectPackage?: IJSONFriendlyObjectPackage;
	suggestedLinks?: Array<any>;
	title?: string;
	subtitle?: string;
	about?: Array<PortableTextBlock>;
	announcements?: SanityAnnouncement[];
	researchLinks?: SanityPromo[];
	relatedContent?: RelatedContent;
	segments?: Array<BreadcrumbSegment>;
	perLoad?: number;
	meta: SanityMetadataFields;
}
